// ImportParsersTable — read-only list of email-import parsers visible
// to this tenant (global ∪ company-specific). Custom parsers come from
// support; the contact-link below the table opens a pre-filled mailto.

import { Card } from '../../../../components/ui/Card/Card';
import { Spinner } from '../../../../components/ui/Spinner/Spinner';
import { useImportParsers } from '../hooks/useCommunicationSettings';
import styles from './ImportParsersTable.module.css';

const CONTACT_HREF =
  'mailto:support@tricivo.com?subject=Parser%20Request&body=Please%20add%20a%20parser%20for%3A%20';

export function ImportParsersTable(): JSX.Element {
  const query = useImportParsers();

  return (
    <Card title="Email Import Parsers">
      <p className={styles.description}>
        These parsers extract booking details from inbound enquiry emails.
        Global parsers ship with the platform; custom parsers are tenant-
        specific.
      </p>

      {query.isLoading ? (
        <div className={styles.loading}>
          <Spinner />
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table} aria-label="Email import parsers">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Source</th>
                <th scope="col">Scope</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {(query.data?.parsers ?? []).length === 0 ? (
                <tr>
                  <td colSpan={4} className={styles.empty}>
                    No parsers available.
                  </td>
                </tr>
              ) : (
                (query.data?.parsers ?? []).map((p) => (
                  <tr key={p.id} data-testid={`parser-row-${p.id}`}>
                    <td>{p.name}</td>
                    <td>
                      <code>{p.source_identifier}</code>
                    </td>
                    <td>
                      <span
                        className={
                          p.company_id === null
                            ? styles.scopeGlobal
                            : styles.scopeCustom
                        }
                      >
                        {p.company_id === null ? 'Global' : 'Custom'}
                      </span>
                    </td>
                    <td>
                      <span
                        className={p.is_active ? styles.active : styles.inactive}
                      >
                        {p.is_active ? 'Active ✓' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <p className={styles.contact}>
        Need a new parser for a different email format?{' '}
        <a href={CONTACT_HREF} className={styles.contactLink}>
          Contact us
        </a>
      </p>
    </Card>
  );
}
